import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseURL } from './apiConfig';

const api = axios.create({
  baseURL: getApiBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies (HttpOnly)
});

// Flag para evitar loops infinitos de refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Interceptor for requests (Validation/Logging only)
api.interceptors.request.use((config) => {
  // Log request for debugging (simpler version)
  if (process.env.NODE_ENV === 'development') {
    console.log('🌐 API Request:', config.method?.toUpperCase(), config.url);
  }
  return config;
}, (error) => {
  console.error('❌ Request Error:', error);
  return Promise.reject(error);
});

// Response Interceptor com refresh automático
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    // Retry on 425 Too Early (TLS 0-RTT replay protection) OR Network Error on Idempotent methods (masked 425)
    // When 425 happens, CORS headers might be missing, causing browser to report "Network Error"
    const is425 = error.response?.status === 425;
    const isNetworkError = error.code === 'ERR_NETWORK' && !error.response;
    const method = error.config?.method?.toLowerCase() || '';
    const isIdempotent = ['get', 'head', 'options'].includes(method);

    if ((is425 || (isNetworkError && isIdempotent)) && error.config) {
      // @ts-ignore
      if (!error.config._retry) {
        console.warn(`⚠️ [API] Retrying request in 1s (Status: ${error.response?.status || error.code})...`, error.config.url);

        // @ts-ignore
        error.config._retry = true;

        // Adicionar delay para evitar travar a UI em caso de loop rápido
        await new Promise(resolve => setTimeout(resolve, 1000));

        try {
          return await api.request(error.config);
        } catch (retryError) {
          return Promise.reject(retryError);
        }
      } else {
        console.warn(`🛑 [API] Retry limit reached for ${error.config.url}`);
      }
    }

    // Authentication disabled by user request - no 401 handling
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

export default api;
